/**
 * TanStack Query Client Configuration
 */
import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './client';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 30 minutes
      gcTime: 30 * 60 * 1000,
      // Retry failed requests 3 times with exponential backoff
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Don't refetch on window focus for data-intensive app
      refetchOnWindowFocus: false,
      // Refetch when network reconnects
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
      onError: (error) => {
        // Global error handling for mutations
        console.error('Mutation error:', error);
      },
    },
  },
});

// Export query key factories for consistent key management
export const queryKeys = {
  // Network queries
  network: {
    all: ['network'] as const,
    graph: (filters: Record<string, unknown>) => [...queryKeys.network.all, 'graph', filters] as const,
    person: (id: string) => [...queryKeys.network.all, 'person', id] as const,
    filterOptions: () => [...queryKeys.network.all, 'filterOptions'] as const,
  },
  // CRM queries
  crm: {
    all: ['crm'] as const,
    stages: ()                              => [...queryKeys.crm.all, 'stages'] as const,
    organizations: ()                       => [...queryKeys.crm.all, 'organizations'] as const,
    contacts: ()                            => [...queryKeys.crm.all, 'contacts'] as const,
    activities: (kind: string, id: string) => [...queryKeys.crm.all, 'activities', kind, id] as const,
    orgContacts: (orgId: string)           => [...queryKeys.crm.all, 'orgContacts', orgId] as const,
    overlaysCompanies: (ids: string[])     => [...queryKeys.crm.all, 'overlays', 'companies', ids] as const,
    overlaysPeople: (ids: string[])        => [...queryKeys.crm.all, 'overlays', 'people', ids] as const,
  },
  // Database (lineage) queries — Companies + People list pages + detail pages.
  database: {
    all: ['database'] as const,
    companies: (filters: Record<string, unknown>) =>
      [...queryKeys.database.all, 'companies', filters] as const,
    people: (filters: Record<string, unknown>) =>
      [...queryKeys.database.all, 'people', filters] as const,
    // careerStrips is keyed on the ordered persona-id list so paging
    // back to a page already-seen hits the cache verbatim.
    careerStrips: (personaIds: string[]) =>
      [...queryKeys.database.all, 'careerStrips', personaIds] as const,
    company: (id: string) =>
      [...queryKeys.database.all, 'company', id] as const,
    persona: (id: string) =>
      [...queryKeys.database.all, 'persona', id] as const,
    personaCareer: (id: string) =>
      [...queryKeys.database.all, 'personaCareer', id] as const,
    personaRelationships: (id: string, params: Record<string, unknown>) =>
      [...queryKeys.database.all, 'personaRelationships', id, params] as const,
  },
  // Radar (spinout) queries
  radar: {
    all: ['radar'] as const,
    spinout: (id: string) =>
      [...queryKeys.radar.all, 'spinout', id] as const,
    personas: (params: Record<string, unknown>) =>
      [...queryKeys.radar.all, 'personas', params] as const,
    candidates: (params: Record<string, unknown>) =>
      [...queryKeys.radar.all, 'candidates', params] as const,
  },
  // Network hub + graph queries
  hub: {
    all: ['hub'] as const,
    firm: (id: string) =>
      [...queryKeys.hub.all, 'firm', id] as const,
    person: (id: string) =>
      [...queryKeys.hub.all, 'person', id] as const,
    browse: () =>
      [...queryKeys.hub.all, 'browse'] as const,
    globalGraph: () =>
      [...queryKeys.hub.all, 'globalGraph'] as const,
    scorecardFirm: (id: string) =>
      [...queryKeys.hub.all, 'scorecard', 'firm', id] as const,
    scorecardPerson: (id: string) =>
      [...queryKeys.hub.all, 'scorecard', 'person', id] as const,
  },
  // Home dashboard queries — coverage snapshot + the briefing modules
  // (radar / network slices) shown on the landing page.
  home: {
    all: ['home'] as const,
    coverage: () => [...queryKeys.home.all, 'coverage'] as const,
    signals: () => [...queryKeys.home.all, 'signals'] as const,
    firms: () => [...queryKeys.home.all, 'firms'] as const,
  },
  // User queries
  user: {
    all: ['user'] as const,
    profile: () => [...queryKeys.user.all, 'profile'] as const,
    preferences: () => [...queryKeys.user.all, 'preferences'] as const,
  },
  // Admin center queries
  admin: {
    all: ['admin'] as const,
    waitlist: {
      all: () => [...queryKeys.admin.all, 'waitlist'] as const,
      stats: () => [...queryKeys.admin.waitlist.all(), 'stats'] as const,
      subscribers: (params: Record<string, unknown>) => [...queryKeys.admin.waitlist.all(), 'subscribers', params] as const,
      subscriber: (id: number) => [...queryKeys.admin.waitlist.all(), 'subscriber', id] as const,
    },
    emails: {
      all: () => [...queryKeys.admin.all, 'emails'] as const,
      templates: () => [...queryKeys.admin.emails.all(), 'templates'] as const,
      versions: (templateId: number) => [...queryKeys.admin.emails.all(), 'versions', templateId] as const,
    },
    campaigns: {
      all: () => [...queryKeys.admin.all, 'campaigns'] as const,
      list: (params: Record<string, unknown>) => [...queryKeys.admin.campaigns.all(), 'list', params] as const,
    },
    analytics: {
      all: () => [...queryKeys.admin.all, 'analytics'] as const,
      ga4Summary: (days: number) => [...queryKeys.admin.analytics.all(), 'ga4Summary', days] as const,
      ga4TopSources: (days: number) => [...queryKeys.admin.analytics.all(), 'ga4TopSources', days] as const,
      ga4TopPages: (days: number) => [...queryKeys.admin.analytics.all(), 'ga4TopPages', days] as const,
      ga4Timeseries: (days: number) => [...queryKeys.admin.analytics.all(), 'ga4Timeseries', days] as const,
      ga4TopCountries: (days: number) => [...queryKeys.admin.analytics.all(), 'ga4TopCountries', days] as const,
      ga4DeviceBreakdown: (days: number) => [...queryKeys.admin.analytics.all(), 'ga4DeviceBreakdown', days] as const,
      ga4Events: (days: number) => [...queryKeys.admin.analytics.all(), 'ga4Events', days] as const,
      ga4Overview: (days: number) => [...queryKeys.admin.analytics.all(), 'ga4Overview', days] as const,
    },
    pipelines: {
      all: () => [...queryKeys.admin.all, 'pipelines'] as const,
      list: (params: Record<string, unknown>) => [...queryKeys.admin.pipelines.all(), 'list', params] as const,
      detail: (pipelineId: string) => [...queryKeys.admin.pipelines.all(), 'detail', pipelineId] as const,
      connectors: () => [...queryKeys.admin.pipelines.all(), 'connectors'] as const,
      destinations: () => [...queryKeys.admin.pipelines.all(), 'destinations'] as const,
      runs: (pipelineId: string) => [...queryKeys.admin.pipelines.all(), 'runs', pipelineId] as const,
      runDetail: (pipelineId: string, runId: string) =>
        [...queryKeys.admin.pipelines.all(), 'runDetail', pipelineId, runId] as const,
      overview: (params?: Record<string, unknown>) =>
        [...queryKeys.admin.pipelines.all(), 'overview', params ?? {}] as const,
      // Phase D.2 — raw_records inspection
      rawRecords: (pipelineId: string, runId: string, params?: Record<string, unknown>) =>
        [...queryKeys.admin.pipelines.all(), 'rawRecords', pipelineId, runId, params ?? {}] as const,
      runEvents: (pipelineId: string, runId: string) =>
        [...queryKeys.admin.pipelines.all(), pipelineId, 'runs', runId, 'events'] as const,
    },
    flags: {
      all: () => [...queryKeys.admin.all, 'flags'] as const,
      list: () => [...queryKeys.admin.flags.all(), 'list'] as const,
    },
    audit: {
      all: () => [...queryKeys.admin.all, 'audit'] as const,
      list: (params: Record<string, unknown>) => [...queryKeys.admin.audit.all(), 'list', params] as const,
    },
    users: {
      all: () => [...queryKeys.admin.all, 'users'] as const,
      list: (params: Record<string, unknown>) => [...queryKeys.admin.users.all(), 'list', params] as const,
    },
    appUsers: {
      all: () => [...queryKeys.admin.all, 'appUsers'] as const,
      list: (params: Record<string, unknown>) => [...queryKeys.admin.appUsers.all(), 'list', params] as const,
      detail: (userId: string) => [...queryKeys.admin.appUsers.all(), 'detail', userId] as const,
      invitations: () => [...queryKeys.admin.appUsers.all(), 'invitations'] as const,
    },
    system: {
      all: () => [...queryKeys.admin.all, 'system'] as const,
      backgroundJobs: (params?: { stuck_threshold_minutes?: number }) =>
        [...queryKeys.admin.system.all(), 'background-jobs', params ?? {}] as const,
    },
    ops: {
      all: () => [...queryKeys.admin.all, 'ops'] as const,
      home: () => [...queryKeys.admin.ops.all(), 'home'] as const,
      alertRules: () => [...queryKeys.admin.ops.all(), 'alertRules'] as const,
      costDeepDive: () => [...queryKeys.admin.ops.all(), 'costDeepDive'] as const,
      runs: (filters: Record<string, unknown>) =>
        [...queryKeys.admin.ops.all(), 'runs', filters] as const,
      runsStats: () => [...queryKeys.admin.ops.all(), 'runsStats'] as const,
      provenance: (entityType: string, entityId: string) =>
        [...queryKeys.admin.ops.all(), 'provenance', entityType, entityId] as const,
      provenanceTypes: () => [...queryKeys.admin.ops.all(), 'provenanceTypes'] as const,
      connectors: (days: number) =>
        [...queryKeys.admin.ops.all(), 'connectors', days] as const,
      connectorTimeline: (pipelineId: string, days: number) =>
        [...queryKeys.admin.ops.all(), 'connectorTimeline', pipelineId, days] as const,
    },
    scheduler: {
      all: () => [...queryKeys.admin.all, 'scheduler'] as const,
      jobs: () => [...queryKeys.admin.scheduler.all(), 'jobs'] as const,
      job: (jobId: string) => [...queryKeys.admin.scheduler.all(), 'job', jobId] as const,
    },
    workspaces: {
      all: () => [...queryKeys.admin.all, 'workspaces'] as const,
      list: (params: Record<string, unknown>) => [...queryKeys.admin.workspaces.all(), 'list', params] as const,
      detail: (id: string) => [...queryKeys.admin.workspaces.all(), 'detail', id] as const,
    },
    erReview: {
      all: () => [...queryKeys.admin.all, 'erReview'] as const,
      clusters: (params: Record<string, unknown>) =>
        [...queryKeys.admin.erReview.all(), 'clusters', params] as const,
      scraped: (params: Record<string, unknown>) =>
        [...queryKeys.admin.erReview.all(), 'scraped', params] as const,
      // Phase D.1 — unified inbox
      inbox: (params?: Record<string, unknown>) =>
        [...queryKeys.admin.erReview.all(), 'inbox', params ?? {}] as const,
    },
  },
} as const;


